/**
 * CalendarView Component - Google Calendar-style view with day, week, and month modes
 *
 * Features:
 * - Three view modes: Day, Week, Month
 * - Click-to-create events on empty time slots
 * - 15-minute granularity for day/week views
 * - Full 24-hour display with AM/PM indicators
 * - Event filtering and calendar selection
 * - Responsive design with mobile support
 * - RSVP and attendance tracking
 *
 * @example
 * <CalendarView />
 */

import React, { useState } from 'react';
import { useCalendar } from '../../contexts/CalendarContext';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  addMinutes,
  setHours,
  setMinutes
} from 'date-fns';
import {
  parseUTCString,
  isEventOnDate,
  isEventInTimeSlot
} from '../../utils/dateUtils';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import CalendarEventCard from './CalendarEventCard';
import CalendarSelector from './CalendarSelector';
import CalendarLegend from './CalendarLegend';
import EventDetailsModal from './EventDetailsModal';
import EventCreationModal from './EventCreationModal';
import EventEditModal from './EventEditModal';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';
import Button from '../Button';
import styles from './CalendarView.module.css';

const MAX_VISIBLE_EVENTS = 3; // Maximum events shown in month view cell
const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0-23 hours
const QUARTER_HOURS = [0, 15, 30, 45]; // 15-minute intervals

const CalendarView = () => {
  const {
    events,
    eventsLoading,
    eventsError,
    selectedCalendarIds,
    availableCalendars,
    updateDateRange
  } = useCalendar();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [creationData, setCreationData] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Refs for auto-scrolling
  const dayViewGridRef = React.useRef(null);
  const weekViewGridRef = React.useRef(null);

  // Update the visible date range based on view mode and current date
  const updateVisibleDateRange = React.useCallback(() => {
    let start, end;

    if (viewMode === 'day') {
      // Show just this day
      start = startOfDay(currentDate);
      end = endOfDay(currentDate);
    } else if (viewMode === 'week') {
      // Show this week (Sunday to Saturday)
      start = startOfWeek(currentDate);
      end = endOfWeek(currentDate);
    } else {
      // Month view - show full month plus surrounding weeks
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      start = startOfWeek(monthStart);
      end = endOfWeek(monthEnd);
    }

    // Update the date range in context, which will trigger event reload
    if (updateDateRange) {
      updateDateRange(start, end);
    }
  }, [viewMode, currentDate, updateDateRange]);

  // Load events whenever view mode or current date changes
  React.useEffect(() => {
    updateVisibleDateRange();
  }, [updateVisibleDateRange]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCloseModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleCloseCreationModal = () => {
    setShowCreationModal(false);
    setCreationData(null);
  };

  const handleEventUpdate = () => {
    // Reload events after RSVP/attendance update
    updateVisibleDateRange();
  };

  const handleEventEdit = (event) => {
    // Close the details modal
    setShowEventModal(false);

    // Open the full-screen edit modal
    setSelectedEvent(event);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedEvent(null);
  };

  const handleEventSaved = () => {
    // Reload events after edit
    handleCloseEditModal();
    updateVisibleDateRange();
  };

  const handleEventCreated = () => {
    // Reload events after creation
    handleCloseCreationModal();
    // Date range update will trigger reload automatically
    updateVisibleDateRange();
  };

  const handleAttendanceUpdate = () => {
    // Date range update will trigger reload automatically
    updateVisibleDateRange();
  };

  // Debug: Log events when they change
  React.useEffect(() => {
    if (events && events.length > 0) {
      console.log('[CalendarView] Events loaded:', events.length);
      events.forEach(event => {
        const parsed = parseUTCString(event.start_time);
        console.log('[CalendarView] Event:', {
          title: event.title,
          start_time_utc: event.start_time,
          start_time_local: format(parsed, 'yyyy-MM-dd HH:mm:ss'),
          calendar_id: event.calendar_id
        });
      });
    } else {
      console.log('[CalendarView] No events loaded');
    }
  }, [events]);

  // Auto-scroll to 6 AM on mount or view change
  React.useEffect(() => {
    const scrollToHour = (gridRef, hour = 6) => {
      if (gridRef.current) {
        // Each hour block is 60px (4 slots * 15px each)
        const scrollPosition = hour * 60;
        gridRef.current.scrollTop = scrollPosition;
      }
    };

    if (viewMode === 'day' && dayViewGridRef.current) {
      scrollToHour(dayViewGridRef, 6);
    } else if (viewMode === 'week' && weekViewGridRef.current) {
      scrollToHour(weekViewGridRef, 6);
    }
  }, [viewMode, currentDate]);

  // Check if user has permission to create events
  const canCreateEvents = () => {
    if (selectedCalendarIds.length === 0) {
      return false;
    }

    // Check if any selected calendar allows event creation
    // Note: can_edit permission implies ability to create and edit events
    return availableCalendars.some(cal => {
      if (!selectedCalendarIds.includes(cal.id)) return false;
      return cal.can_edit === true;
    });
  };

  // Handle time slot click for event creation
  const handleTimeSlotClick = (date, hour, minute = 0) => {
    if (!canCreateEvents()) return;

    // Create start time
    const startTime = setMinutes(setHours(date, hour), minute);

    // Default duration: 1 hour
    const endTime = addMinutes(startTime, 60);

    // Get the first selected calendar that allows event creation
    const creatableCalendar = availableCalendars.find(cal => {
      if (!selectedCalendarIds.includes(cal.id)) return false;
      return cal.can_edit === true;
    });

    setCreationData({
      calendarId: creatableCalendar?.id || selectedCalendarIds[0],
      date: date,
      startTime: startTime,
      endTime: endTime
    });
    setShowCreationModal(true);
  };

  // Handle day click in month view
  const handleDayClick = (date) => {
    if (!canCreateEvents()) return;

    // Default time: 9 AM - 10 AM
    const startTime = setMinutes(setHours(date, 9), 0);
    const endTime = addMinutes(startTime, 60);

    const creatableCalendar = availableCalendars.find(cal => {
      if (!selectedCalendarIds.includes(cal.id)) return false;
      return cal.can_edit === true;
    });

    setCreationData({
      calendarId: creatableCalendar?.id || selectedCalendarIds[0],
      date: date,
      startTime: startTime,
      endTime: endTime,
      requiresTimeSelection: true // Flag to show time must be selected
    });
    setShowCreationModal(true);
  };

  // Navigation
  const goToPrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, -7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addDays(currentDate, 7));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => isEventOnDate(event.start_time, date));
  };

  // Get events for a specific time slot
  const getEventsForTimeSlot = (date, hour, minute) => {
    return events.filter(event => isEventInTimeSlot(event.start_time, date, hour, minute));
  };

  // Format hour for display
  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  // Render Day View
  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);
    
    // Separate all-day events from timed events
    const allDayEvents = dayEvents.filter(event => event.all_day);
    const timedEvents = dayEvents.filter(event => !event.all_day);

    return (
      <div className={styles.dayView}>
        <div className={styles.dayViewHeader}>
          <div className={styles.dayViewDate}>
            <h3>{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
            {dayEvents.length > 0 && (
              <span className={styles.eventCount}>
                {dayEvents.length} {dayEvents.length === 1 ? 'event' : 'events'}
              </span>
            )}
          </div>
        </div>

        {/* All-day events section */}
        {allDayEvents.length > 0 && (
          <div className={styles.allDaySection}>
            <div className={styles.allDayLabel}>All-day events</div>
            <div className={styles.allDayEvents}>
              {allDayEvents.map(event => (
                <button
                  key={event.id}
                  className={styles.allDayEvent}
                  style={{ '--event-color': event.color_code || event.calendar_color || 'var(--color-info)' }}
                  onClick={() => handleEventClick(event)}
                >
                  <div
                    className={styles.eventDot}
                    style={{ '--calendar-color': event.color_code || event.calendar_color || 'var(--color-info)' }}
                    aria-hidden="true"
                  />
                  <span className={styles.eventTitle}>{event.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.dayViewGrid} ref={dayViewGridRef}>
          {/* Time column */}
          <div className={styles.timeColumn}>
            {HOURS.map(hour => (
              <div key={hour} className={styles.timeLabel}>
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Events column */}
          <div className={styles.eventsColumn}>
            {HOURS.map(hour => (
              <div key={hour} className={styles.hourBlock}>
                {QUARTER_HOURS.map(minute => {
                  const slotEvents = getEventsForTimeSlot(currentDate, hour, minute);
                  const hasEvents = slotEvents.length > 0;

                  return (
                    <div
                      key={`${hour}-${minute}`}
                      className={`${styles.timeSlot} ${hasEvents ? styles.hasEvents : ''} ${canCreateEvents() ? styles.clickable : ''}`}
                      onClick={() => !hasEvents && handleTimeSlotClick(currentDate, hour, minute)}
                    >
                      <div className={styles.timeSlotEvents}>
                        {slotEvents.map(event => (
                          <div
                            key={event.id}
                            className={styles.dayEvent}
                            style={{ '--event-color': event.color_code || event.calendar_color || 'var(--color-info)' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(event);
                            }}
                          >
                            <div className={styles.eventTime}>
                              {format(parseUTCString(event.start_time), 'h:mm a')}
                            </div>
                            <div className={styles.eventTitle}>{event.title}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className={styles.weekView}>
        <div className={styles.weekViewHeader}>
          <div className={styles.weekTimeHeader}></div>
          {weekDays.map(day => {
            const isToday = isSameDay(day, new Date());
            return (
              <div key={format(day, 'yyyy-MM-dd')} className={`${styles.weekDayHeader} ${isToday ? styles.today : ''}`}>
                <div className={styles.weekDayName}>{format(day, 'EEE')}</div>
                <div className={styles.weekDayNumber}>{format(day, 'd')}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.weekViewGrid} ref={weekViewGridRef}>
          {/* Time column */}
          <div className={styles.weekTimeColumn}>
            {HOURS.map(hour => (
              <div key={hour} className={styles.weekTimeLabel}>
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map(day => (
            <div key={format(day, 'yyyy-MM-dd')} className={styles.weekDayColumn}>
              {HOURS.map(hour => (
                <div key={hour} className={styles.weekHourBlock}>
                  {QUARTER_HOURS.map(minute => {
                    const slotEvents = getEventsForTimeSlot(day, hour, minute);
                    const hasEvents = slotEvents.length > 0;

                    return (
                      <div
                        key={`${hour}-${minute}`}
                        className={`${styles.weekTimeSlot} ${hasEvents ? styles.hasEvents : ''} ${canCreateEvents() ? styles.clickable : ''}`}
                        onClick={() => !hasEvents && handleTimeSlotClick(day, hour, minute)}
                      >
                        <div className={styles.weekTimeSlotEvents}>
                          {slotEvents.map(event => (
                            <div
                              key={event.id}
                              className={styles.weekEvent}
                              style={{ '--event-color': event.color_code || event.calendar_color || 'var(--color-info)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}
                            >
                              <div className={styles.eventTitle}>{event.title}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const monthDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div className={styles.monthView}>
        <div className={styles.monthGrid}>
          {/* Week day headers */}
          {weekDays.map(day => (
            <div key={day} className={styles.monthGridHeader}>
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {monthDays.map(day => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={format(day, 'yyyy-MM-dd')}
                className={`${styles.monthGridDay} ${!isCurrentMonth ? styles.otherMonth : ''} ${isToday ? styles.today : ''} ${canCreateEvents() ? styles.clickable : ''}`}
                onClick={(e) => {
                  // Only trigger creation if clicking on empty space
                  if (e.target === e.currentTarget || e.target.classList.contains(styles.dayNumber)) {
                    handleDayClick(day);
                  }
                }}
              >
                <div className={styles.dayNumber}>{format(day, 'd')}</div>
                <div className={styles.dayEvents}>
                  {dayEvents.slice(0, MAX_VISIBLE_EVENTS).map(event => (
                    <button
                      key={event.id}
                      className={styles.monthEvent}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleEventClick(event);
                        }
                      }}
                      aria-label={`View event: ${event.title}`}
                      type="button"
                    >
                      <div
                        className={styles.eventDot}
                        style={{ '--calendar-color': event.color_code || event.calendar_color || 'var(--color-info)' }}
                        aria-hidden="true"
                      />
                      <span className={styles.eventTitle}>{event.title}</span>
                    </button>
                  ))}
                  {dayEvents.length > MAX_VISIBLE_EVENTS && (
                    <button
                      className={styles.moreEvents}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentDate(day);
                        setViewMode('day');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCurrentDate(day);
                          setViewMode('day');
                        }
                      }}
                      aria-label={`View all ${dayEvents.length} events for ${format(day, 'MMMM d, yyyy')}`}
                      type="button"
                    >
                      +{dayEvents.length - MAX_VISIBLE_EVENTS} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Get current view title
  const getViewTitle = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'MMMM d, yyyy');
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      // Always show range format: "Mar 1 - Mar 7, 2026"
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  return (
    <div className={styles.calendarViewContainer}>
      <div className={styles.calendarViewHeader}>
        <div className={styles.calendarHeaderLeft}>
          <h2 className={styles.calendarViewTitle}>Calendar</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToToday}
          >
            Today
          </Button>
        </div>

        <div className={styles.calendarHeaderCenter}>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevious}
            aria-label="Previous"
            icon={<ChevronLeft size={16} />}
          />
          <h3 className={styles.viewTitle}>{getViewTitle()}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNext}
            aria-label="Next"
            icon={<ChevronRight size={16} />}
          />
        </div>

        <div className={styles.calendarViewActions}>
          <div className={styles.viewModeToggle} role="group" aria-label="View mode">
            <Button
              variant={viewMode === 'day' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('day')}
              aria-label="Day view"
              aria-pressed={viewMode === 'day'}
            >
              Day
            </Button>
            <Button
              variant={viewMode === 'week' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('week')}
              aria-label="Week view"
              aria-pressed={viewMode === 'week'}
            >
              Week
            </Button>
            <Button
              variant={viewMode === 'month' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('month')}
              aria-label="Month view"
              aria-pressed={viewMode === 'month'}
            >
              Month
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.calendarViewContent}>
        <aside className={styles.calendarSidebar} aria-label="Calendar filters">
          <CalendarSelector />
          <CalendarLegend />
        </aside>

        <main className={styles.calendarMain}>
          <div className={styles.calendarEventsContainer}>
            {eventsLoading ? (
              <div className={styles.calendarLoading}>
                <LoadingSpinner />
                <p>Loading events...</p>
              </div>
            ) : eventsError ? (
              <div className={styles.calendarError}>
                <p className={styles.errorMessage}>{eventsError}</p>
                <Button
                  variant="primary"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </Button>
              </div>
            ) : selectedCalendarIds.length === 0 ? (
              <EmptyState
                icon="📅"
                title="Select at least one calendar"
                description="Choose calendars from the sidebar to view events"
                size="medium"
              />
            ) : viewMode === 'day' ? (
              renderDayView()
            ) : viewMode === 'week' ? (
              renderWeekView()
            ) : (
              renderMonthView()
            )}
          </div>
        </main>
      </div>

      {showEventModal && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={handleCloseModal}
          onUpdate={handleEventUpdate}
          onEdit={handleEventEdit}
        />
      )}

      {showCreationModal && creationData && (
        <EventCreationModal
          calendarId={creationData.calendarId}
          initialDate={creationData.date}
          initialStartTime={creationData.startTime}
          initialEndTime={creationData.endTime}
          requiresTimeSelection={creationData.requiresTimeSelection}
          onClose={handleCloseCreationModal}
          onCreate={handleEventCreated}
        />
      )}

      {showEditModal && selectedEvent && (
        <EventEditModal
          event={selectedEvent}
          onClose={handleCloseEditModal}
          onSave={handleEventSaved}
        />
      )}
    </div>
  );
};

export default CalendarView;
